// The start page: a new user is welcomed into a guided first booking, a returning user sees the
// coming bookings with Ändra and Avboka and can repeat the last trip in one step (demo case 1).
// An administrator sees the fleet's state as well.
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { sv } from '@aldorock-fordon/shared/sv';
import type { StartData } from '@aldorock-fordon/shared/api-types';
import { api, type Me } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { Table } from '../ui/Table.tsx';
import { Facts } from '../ui/Facts.tsx';
import { BookingStatusView } from '../ui/Status.tsx';
import { clock, day, num, span, vehicleName } from '../format.ts';

export function StartPage({ me }: { me: Me }) {
  const h1 = usePage(sv.start.title);
  const q = useQuery({ queryKey: ['start'], queryFn: () => api<StartData>('/start') });
  const d = q.data;
  const canBook = me.permissions['booking.own'];
  return (
    <>
      <PageHead help="start" title={sv.start.title} titleRef={h1} />
      {q.isPending ? <p role="status">{sv.app.loading}</p> : null}
      {d ? (
        <>
          {canBook && d.firstTime ? (
            <section className="block first-time" aria-labelledby="first-time">
              <h2 className="block-title" id="first-time">
                {sv.start.welcomeFirst(me.user.displayName)}
              </h2>
              <p>{sv.start.firstIntro}</p>
              <p className="actions-inline">
                <Link className="btn btn-primary" to="/boka">
                  {sv.start.bookFirst}
                </Link>
              </p>
            </section>
          ) : null}
          {canBook && !d.firstTime ? (
            <div className="todo">
              <Link className="btn btn-primary" to="/boka">
                {sv.start.book}
              </Link>
              {d.recent ? (
                <>
                  <span>{sv.start.rebookLine(vehicleName(d.recent), d.recent.poolName)}</span>
                  <Link
                    className="btn"
                    to={`/boka?rebook=${d.recent.vehicleId}&pool=${d.recent.poolId}&from=${encodeURIComponent(d.recent.startsAt)}&to=${encodeURIComponent(d.recent.endsAt)}${d.recent.purpose ? `&purpose=${encodeURIComponent(d.recent.purpose)}` : ''}`}
                  >
                    {sv.start.rebookButton(vehicleName(d.recent))}
                  </Link>
                </>
              ) : null}
            </div>
          ) : null}

          {canBook ? (
            <Table
              caption={sv.start.upcoming}
              id="kommande"
              isEmpty={d.upcoming.length === 0}
              empty={sv.start.upcomingEmpty}
              hasOptional
            >
              <thead>
                <tr>
                  <th scope="col">{sv.columns.date}</th>
                  <th scope="col">{sv.columns.vehicle}</th>
                  <th scope="col" className="col-opt">
                    {sv.columns.pool}
                  </th>
                  <th scope="col" className="col-opt">
                    {sv.columns.driver}
                  </th>
                  <th scope="col">{sv.columns.status}</th>
                  <th scope="col">{sv.columns.action}</th>
                </tr>
              </thead>
              <tbody>
                {d.upcoming.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <span className="num">{day(b.startsAt)}</span>{' '}
                      {sv.range(
                        clock(b.startsAt),
                        day(b.endsAt) === day(b.startsAt)
                          ? clock(b.endsAt)
                          : `${day(b.endsAt)} ${clock(b.endsAt)}`,
                      )}
                    </td>
                    <td>
                      <Link to={`/bokning/${b.id}`}>{vehicleName(b)}</Link>
                    </td>
                    <td className="col-opt">{b.poolName}</td>
                    <td className="col-opt">{b.driver}</td>
                    <td>
                      <BookingStatusView status={b.status} />
                    </td>
                    <td>
                      <span className="actions-inline">
                        <Link
                          className="btn btn-quiet"
                          to={`/bokning/${b.id}`}
                          aria-label={`${sv.start.change} ${b.number}`}
                        >
                          {sv.start.change}
                        </Link>
                        <Link
                          className="btn btn-quiet"
                          to={`/bokning/${b.id}?avboka=1`}
                          aria-label={`${sv.start.cancel} ${b.number}`}
                        >
                          {sv.start.cancel}
                        </Link>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}

          {canBook && d.past.length > 0 ? (
            <Table caption={sv.start.past} id="tidigare" hasOptional>
              <thead>
                <tr>
                  <th scope="col">{sv.columns.date}</th>
                  <th scope="col">{sv.columns.vehicle}</th>
                  <th scope="col" className="col-opt">
                    {sv.columns.purpose}
                  </th>
                  <th scope="col">{sv.columns.status}</th>
                </tr>
              </thead>
              <tbody>
                {d.past.map((b) => (
                  <tr key={b.id}>
                    <td>{span(b.startsAt, b.endsAt)}</td>
                    <td>
                      <Link to={`/bokning/${b.id}`}>{vehicleName(b)}</Link>
                    </td>
                    <td className="col-opt">{b.purpose ?? ''}</td>
                    <td>
                      <BookingStatusView status={b.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}

          {d.admin ? (
            <section className="block" aria-labelledby="fleet">
              <h2 className="block-title" id="fleet">
                {sv.start.adminTitle}
              </h2>
              <div className="cols cols-2">
                <Facts
                  top={false}
                  facts={[
                    {
                      key: 'v',
                      label: sv.start.adminVehicles,
                      value: <span className="num">{num(d.admin.vehicles)}</span>,
                      testId: 'stat-vehicles',
                    },
                    {
                      key: 'a',
                      label: sv.start.adminActive,
                      value: <span className="num">{num(d.admin.activeVehicles)}</span>,
                    },
                    {
                      key: 'p',
                      label: sv.start.adminPools,
                      value: <span className="num">{num(d.admin.pools)}</span>,
                    },
                    {
                      key: 'b',
                      label: sv.start.adminBookingsToday,
                      value: <span className="num">{num(d.admin.bookingsToday)}</span>,
                    },
                  ]}
                />
                <Facts
                  top={false}
                  facts={[
                    {
                      key: 'i',
                      label: sv.start.adminInspections,
                      value: <span className="num">{num(d.admin.inspectionsDue30)}</span>,
                    },
                    {
                      key: 'l',
                      label: sv.start.adminLeases,
                      value: <span className="num">{num(d.admin.leasesEnding90)}</span>,
                    },
                    {
                      key: 'u',
                      label: sv.start.adminNoUnit,
                      value: <span className="num">{num(d.admin.vehiclesWithoutUnit)}</span>,
                    },
                  ]}
                />
              </div>
              <p className="actions-inline">
                <Link className="btn" to="/fordon">
                  {sv.start.adminGo}
                </Link>
              </p>
            </section>
          ) : null}
        </>
      ) : null}
    </>
  );
}
